import type { CompartilhamentoDaVisao, ConfigDaVisao, PapelNaVisao, Visao, VisibilidadeDaVisao } from '@orbind/core'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api.ts'

/**
 * Visões de uma tela (SPEC-20261103-1418): de um quadro (`quadroId`) ou da
 * lista de Itens de Trabalho (`null`). Mesmo hook, mesmas regras.
 */

export type NovaVisao = {
  id?: string
  name: string
  visibility: VisibilidadeDaVisao
  publicRole: PapelNaVisao
  shares: CompartilhamentoDaVisao[]
  config: ConfigDaVisao
}

export type AlteracaoDaVisao = Partial<Omit<NovaVisao, 'id' | 'config'>> & { config?: Partial<ConfigDaVisao> }

export const chaveDasVisoes = (quadroId: string | null) => ['views', quadroId ?? 'itens'] as const

type Corpo = { view?: Visao; views?: Visao[]; error?: { message?: string } } | null

async function ler(r: Response, falha: string) {
  const corpo = (await r.json().catch(() => null)) as Corpo
  if (!r.ok || !corpo) throw new Error(corpo?.error?.message ?? falha)
  return corpo
}

export function useVisoes(quadroId: string | null) {
  return useQuery({
    queryKey: chaveDasVisoes(quadroId),
    queryFn: async (): Promise<Visao[]> => {
      const r = quadroId
        ? await api.kanbans[':id'].views.$get({ param: { id: quadroId } })
        : await api['work-items'].views.$get()
      return (await ler(r as unknown as Response, 'Não foi possível carregar as visões.')).views ?? []
    },
  })
}

export function useMutacoesDeVisao(quadroId: string | null) {
  const queryClient = useQueryClient()
  const chave = chaveDasVisoes(quadroId)
  /** Troca (ou põe) a visão na lista em cache, sem esperar a releitura. */
  const guardar = (visao: Visao) =>
    queryClient.setQueryData<Visao[]>(chave, (lista) => {
      if (!lista) return [visao]
      return lista.some((v) => v.id === visao.id) ? lista.map((v) => (v.id === visao.id ? visao : v)) : [...lista, visao]
    })

  const criar = useMutation({
    mutationFn: async (input: NovaVisao): Promise<Visao> => {
      const r = quadroId
        ? await api.kanbans[':id'].views.$post({ param: { id: quadroId }, json: input as never })
        : await api['work-items'].views.$post({ json: input as never })
      return (await ler(r as unknown as Response, 'Não foi possível criar a visão.')).view!
    },
    onSuccess: guardar,
  })

  const alterar = useMutation({
    mutationFn: async ({ id, ...input }: AlteracaoDaVisao & { id: string }): Promise<Visao> => {
      const r = quadroId
        ? await api.kanbans[':id'].views[':viewId'].$put({ param: { id: quadroId, viewId: id }, json: input as never })
        : await api['work-items'].views[':viewId'].$put({ param: { viewId: id }, json: input as never })
      return (await ler(r as unknown as Response, 'Não foi possível salvar a visão.')).view!
    },
    // na tela já: coluna arrastada ou tipo trocado não podem piscar de volta
    onMutate: ({ id, config, ...resto }) => {
      queryClient.setQueryData<Visao[]>(chave, (lista) =>
        lista?.map((v) => (v.id === id ? { ...v, ...resto, config: { ...v.config, ...config } } : v)),
      )
    },
    onSuccess: guardar,
    onError: () => void queryClient.invalidateQueries({ queryKey: chave }),
  })

  const apagar = useMutation({
    mutationFn: async (id: string) => {
      const r = quadroId
        ? await api.kanbans[':id'].views[':viewId'].$delete({ param: { id: quadroId, viewId: id } })
        : await api['work-items'].views[':viewId'].$delete({ param: { viewId: id } })
      await ler(r as unknown as Response, 'Não foi possível apagar a visão.')
      return id
    },
    onSuccess: (id) => queryClient.setQueryData<Visao[]>(chave, (lista) => lista?.filter((v) => v.id !== id)),
  })

  return { criar, alterar, apagar }
}
